import { useState } from "react";
import { router } from "@inertiajs/react";
import Panel from "@/components/ui/Panel";
import Button from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import { Input } from "@/components/ui/Field";
import Avatar from "@/components/ui/Avatar";

export type TeamMember = {
  id: number;
  email: string;
  name: string;
  role: string | null;
  accepted: boolean;
  avatar: string | null;
  // Present only while pending: the POC pastes it into a DM when email fails.
  claim_url?: string | null;
};

// The point of contact brings in helpers after being picked. Invites are by
// email, so a POC can add friends who don't have accounts here yet - the invite
// links itself to them when they first sign in.
//
// Co-organizers invite and chase invites too, but only the POC removes anyone:
// taking someone's access away is the one team action that isn't reversible by
// the person it happens to.
export default function EventTeam({
  members,
  eventId,
  canRemove,
}: {
  members: TeamMember[];
  eventId: number;
  canRemove: boolean;
}) {
  const [copied, setCopied] = useState<number | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");

  function add(e: React.FormEvent) {
    e.preventDefault();
    router.post(
      `/organize/${eventId}/team`,
      { email, name, role },
      {
        preserveScroll: true,
        onSuccess: () => {
          setEmail("");
          setName("");
          setRole("");
        },
      },
    );
  }

  return (
    <Panel title={`team.csv - ${members.length}`}>
      <div className="flex flex-col gap-4">
        {members.length === 0 ? (
          <p className="text-body text-ink-muted">
            You're the point of contact. Add anyone helping you run this one -
            they'll see the event and the checklist too.
          </p>
        ) : (
          <div className="flex flex-col">
            {members.map((member) => (
              <div
                key={member.id}
                className="border-line/20 flex items-center gap-3 border-b py-2.5 last:border-b-0"
              >
                <Avatar
                  src={member.avatar}
                  name={member.name}
                  seed={member.email}
                  size="sm"
                />

                <div className="min-w-0 flex-1">
                  <p className="text-body text-ink truncate font-bold">
                    {member.name}
                    {member.role && (
                      <span className="text-ink-muted font-normal">
                        {" "}
                        · {member.role}
                      </span>
                    )}
                  </p>
                  {member.name !== member.email && (
                    <p className="text-caption text-ink-soft truncate">
                      {member.email}
                    </p>
                  )}
                </div>

                {member.accepted ? (
                  <Badge tone="success">Joined</Badge>
                ) : (
                  <Badge tone="neutral">Invited</Badge>
                )}

                {!member.accepted && member.claim_url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard?.writeText(member.claim_url!);
                      setCopied(member.id);
                      setTimeout(() => setCopied(null), 2000);
                    }}
                  >
                    {copied === member.id ? "Copied!" : "Copy link"}
                  </Button>
                )}

                {!member.accepted && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      router.post(
                        `/organize/${eventId}/team/${member.id}/resend`,
                        {},
                        { preserveScroll: true },
                      )
                    }
                  >
                    Resend
                  </Button>
                )}

                {canRemove && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Remove ${member.name} from your team?`)) {
                        router.delete(
                          `/organize/${eventId}/team/${member.id}`,
                          {
                            preserveScroll: true,
                          },
                        );
                      }
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <form
          onSubmit={add}
          className="border-line/20 flex flex-wrap items-end gap-2 border-t pt-4"
        >
          <div className="min-w-48 flex-1">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="their@email.com"
              required
            />
          </div>
          <div className="w-32">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
            />
          </div>
          <div className="w-32">
            <Input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Role"
            />
          </div>
          <Button type="submit">Add</Button>
        </form>

        <p className="text-caption text-ink-soft">
          They don't need an account yet, and the account they sign in with
          doesn't have to match the address you invite - the link connects them
          either way.
        </p>
      </div>
    </Panel>
  );
}
